import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { Dialog, Button as VButton, FormItem } from 'vnetwork-ui'
import dayjs, { Dayjs } from 'dayjs'
import { Calendar, Clock, FileText, Zap } from 'lucide-react'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'

import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import AsyncSelectWithServer from '@/components/Form/Shared/AsyncSelectWithServer'
import DateTimePicker from '@/components/Form/DateTimePicker'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import {
    createDowntimeLog,
    updateDowntimeLog,
} from '@/modules/Service/services/sla'
import {
    getErrorTicketSelectOptions,
    getTicketByID,
} from '@/modules/Ticket/services'
import { Ticket } from '@/modules/Ticket/types/response'
import {
    SLADowntimeLog,
    DowntimeLogPayload,
    DOWNTIME_TYPE_LABELS,
    DOWNTIME_CAUSE_LABELS,
    DOWNTIME_CAUSE_COUNTS_SLA,
} from '@/modules/Service/types/sla'

interface DowntimeFormModalProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    serviceId: number
    downtime?: SLADowntimeLog | null
}

// Form values use Dayjs for datetime fields
interface DowntimeFormValues extends Omit<
    DowntimeLogPayload,
    'downtime_start' | 'downtime_end'
> {
    downtime_start: Dayjs | null
    downtime_end: Dayjs | null
}

interface OverlapErrorResponse {
    error: string
    conflicting_id: number
}

const DOWNTIME_TYPE_OPTIONS = [
    { value: 'unplanned', label: DOWNTIME_TYPE_LABELS.unplanned },
    { value: 'planned', label: DOWNTIME_TYPE_LABELS.planned },
    { value: 'maintenance', label: DOWNTIME_TYPE_LABELS.maintenance },
    { value: 'emergency', label: DOWNTIME_TYPE_LABELS.emergency },
]

const DOWNTIME_CAUSE_OPTIONS = [
    {
        value: 'provider',
        label: `${DOWNTIME_CAUSE_LABELS.provider} - DC, network, hardware`,
    },
    {
        value: 'customer',
        label: `${DOWNTIME_CAUSE_LABELS.customer} - Cấu hình sai, chưa thanh toán`,
    },
    {
        value: 'third_party',
        label: `${DOWNTIME_CAUSE_LABELS.third_party} - ISP, cloud provider bên ngoài`,
    },
    {
        value: 'force_majeure',
        label: `${DOWNTIME_CAUSE_LABELS.force_majeure} - Thiên tai, mất điện lưới`,
    },
    {
        value: 'scheduled',
        label: `${DOWNTIME_CAUSE_LABELS.scheduled} - Bảo trì đã thông báo trước`,
    },
]

/**
 * If the error is a 409 downtime-overlap-detected, returns the conflicting log id.
 * Otherwise returns null.
 */
function getOverlapConflictID(error: unknown): number | null {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError<OverlapErrorResponse>
        if (
            axiosError.response?.status === 409 &&
            axiosError.response?.data?.error === 'downtime-overlap-detected'
        ) {
            return axiosError.response.data.conflicting_id ?? null
        }
    }
    return null
}

/** Extract a human-readable message from any AxiosError or Error. */
function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError<Record<string, unknown>>
        const data = axiosError.response?.data
        if (data && typeof data === 'object') {
            const desc =
                data.error_description ??
                data.message ??
                data.detail ??
                data.error
            if (typeof desc === 'string') return desc
        }
    }
    if (error instanceof Error) return error.message
    return 'An error occurred'
}

const DowntimeFormModal: React.FC<DowntimeFormModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    serviceId,
    downtime,
}) => {
    const { t } = useTranslation('services')
    const isEditing = !!downtime

    const { control, handleSubmit, reset, watch, setValue } =
        useForm<DowntimeFormValues>({
            defaultValues: {
                downtime_type: 'unplanned',
                downtime_cause: 'provider',
                downtime_start: dayjs(),
                downtime_end: null,
                downtime_description: '',
                root_cause: '',
                counts_against_sla: true,
                excluded_reason: '',
            },
        })

    const countsAgainstSLA = watch('counts_against_sla')
    const downtimeCause = watch('downtime_cause')
    const selectedTicketId = watch('ticket_id')

    // Fetch selected ticket details
    const { data: ticketData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_DETAIL, String(selectedTicketId)],
        queryFn: () => getTicketByID({ ticket_id: Number(selectedTicketId) }),
        enabled: !!selectedTicketId,
    })
    const selectedTicket = ticketData?.result as Ticket | undefined

    // Auto-fill from ticket
    const handleAutofillFromTicket = () => {
        if (!selectedTicket) return

        if (selectedTicket.ticket_requested_at) {
            setValue(
                'downtime_start',
                dayjs(selectedTicket.ticket_requested_at)
            )
        }
        if (selectedTicket.ticket_content && !watch('downtime_description')) {
            setValue('downtime_description', selectedTicket.ticket_content)
        }
        if (selectedTicket.ticket_root_cause && !watch('root_cause')) {
            setValue('root_cause', selectedTicket.ticket_root_cause)
        }
    }

    // Auto-set counts_against_sla based on cause
    useEffect(() => {
        if (downtimeCause) {
            const shouldCount = DOWNTIME_CAUSE_COUNTS_SLA[downtimeCause]
            setValue('counts_against_sla', shouldCount)
        }
    }, [downtimeCause, setValue])

    // Reset form when downtime changes
    useEffect(() => {
        if (downtime) {
            reset({
                downtime_type: downtime.downtime_type,
                downtime_cause: downtime.downtime_cause || 'provider',
                downtime_start: downtime.downtime_start
                    ? dayjs(downtime.downtime_start)
                    : null,
                downtime_end: downtime.downtime_end
                    ? dayjs(downtime.downtime_end)
                    : null,
                downtime_description: downtime.downtime_description || '',
                root_cause: downtime.root_cause || '',
                counts_against_sla: downtime.counts_against_sla,
                excluded_reason: downtime.excluded_reason || '',
                ticket_id: downtime.ticket_id,
            })
        } else {
            reset({
                downtime_type: 'unplanned',
                downtime_cause: 'provider',
                downtime_start: dayjs(),
                downtime_end: null,
                downtime_description: '',
                root_cause: '',
                counts_against_sla: true,
                excluded_reason: '',
            })
        }
    }, [downtime, reset])

    /**
     * Custom error handler for create/update mutations.
     * - 409 overlap → show specific Vietnamese message with conflicting log ID.
     * - All other errors → show generic error toast (since showErrorToast is false
     *   on the mutation, we must emit it here to avoid silent failures).
     */
    const handleMutationError = (error: unknown) => {
        const conflictID = getOverlapConflictID(error)
        if (conflictID !== null) {
            toast.error(
                `Khoảng thời gian downtime trùng với log #${conflictID}. Chỉnh sửa log hiện có thay vì tạo mới.`
            )
            return
        }
        // Non-overlap errors: surface the message so they are never silent
        toast.error(extractErrorMessage(error))
    }

    // Create mutation — showErrorToast:false so handleMutationError owns all toasts
    const { mutate: create, isPending: isCreating } = useMutationWithHandler(
        {
            mutationFn: (payload: DowntimeLogPayload) =>
                createDowntimeLog(serviceId, payload),
        },
        {
            successMessage: t('sla.downtime_created', 'Đã ghi nhận downtime'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SLA_DOWNTIME_WITH_STATS,
            onError: handleMutationError,
            showErrorToast: false,
        }
    )

    // Update mutation — showErrorToast:false so handleMutationError owns all toasts
    const { mutate: update, isPending: isUpdating } = useMutationWithHandler(
        {
            mutationFn: (payload: DowntimeLogPayload) =>
                updateDowntimeLog(
                    serviceId,
                    downtime!.downtime_log_id,
                    payload
                ),
        },
        {
            successMessage: t('sla.downtime_updated', 'Đã cập nhật downtime'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SLA_DOWNTIME_WITH_STATS,
            onError: handleMutationError,
            showErrorToast: false,
        }
    )

    const isPending = isCreating || isUpdating

    const onSubmit = (data: DowntimeFormValues) => {
        const payload: DowntimeLogPayload = {
            ...data,
            downtime_start: data.downtime_start
                ? data.downtime_start.toISOString()
                : '',
            downtime_end: data.downtime_end
                ? data.downtime_end.toISOString()
                : undefined,
        }

        const mutation = isEditing ? update : create
        mutation(payload, {
            onSuccess: () => {
                onSuccess()
            },
        })
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title={
                isEditing
                    ? t('sla.edit_downtime', 'Sửa Downtime Log')
                    : t('sla.log_downtime', 'Ghi nhận Downtime')
            }
            actions={
                <div className="flex items-center gap-2">
                    <VButton variant="outlined" onClick={onClose}>
                        {t('common.cancel', 'Hủy')}
                    </VButton>
                    <VButton
                        onClick={handleSubmit(onSubmit)}
                        loading={isPending}
                    >
                        {isEditing
                            ? t('common.save', 'Lưu')
                            : t('common.create', 'Tạo')}
                    </VButton>
                </div>
            }
            className="max-w-2xl"
        >
            <form className="space-y-4">
                {/* Ticket ID - Search first for autofill */}
                <div className="space-y-2">
                    <AsyncSelectWithServer
                        control={control}
                        name="ticket_id"
                        label={t(
                            'sla.related_ticket',
                            'Ticket liên quan (tùy chọn)'
                        )}
                        placeholder={t(
                            'sla.search_ticket',
                            'Tìm ticket theo ID hoặc nội dung...'
                        )}
                        asyncOptions={getErrorTicketSelectOptions()}
                    />

                    {/* Ticket Preview & Autofill */}
                    {selectedTicket && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-900">
                                        Ticket #{selectedTicket.ticket_id}
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className="text-xs"
                                    >
                                        {selectedTicket.ticket_type_title}
                                    </Badge>
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleAutofillFromTicket}
                                    className="text-xs"
                                >
                                    <Zap className="w-3 h-3 mr-1" />
                                    Autofill
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs">
                                {selectedTicket.ticket_requested_at && (
                                    <div className="flex items-center gap-1.5 text-gray-600">
                                        <Calendar className="w-3.5 h-3.5" />
                                        <span>Bắt đầu:</span>
                                        <span className="font-medium text-gray-900">
                                            {dayjs(
                                                selectedTicket.ticket_requested_at
                                            ).format('DD/MM/YYYY HH:mm')}
                                        </span>
                                    </div>
                                )}
                                {selectedTicket.ticket_status && (
                                    <div className="flex items-center gap-1.5 text-gray-600">
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>Trạng thái:</span>
                                        <Badge
                                            variant="secondary"
                                            className="text-xs"
                                        >
                                            {selectedTicket.ticket_status}
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            {selectedTicket.ticket_root_cause && (
                                <div className="text-xs text-gray-600 pt-1 border-t border-blue-200">
                                    <span className="font-medium">
                                        Root cause:
                                    </span>{' '}
                                    <span className="line-clamp-2">
                                        {selectedTicket.ticket_root_cause}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Type */}
                <FormItem
                    control={control}
                    name="downtime_type"
                    type="select"
                    label={t('sla.downtime_type', 'Loại Downtime')}
                    placeholder="Chọn loại"
                    options={DOWNTIME_TYPE_OPTIONS}
                    allowClear={false}
                    size="small"
                />

                {/* Cause */}
                <FormItem
                    control={control}
                    name="downtime_cause"
                    type="select"
                    label={t('sla.downtime_cause', 'Nguyên nhân')}
                    placeholder="Chọn nguyên nhân"
                    options={DOWNTIME_CAUSE_OPTIONS}
                    allowClear={false}
                    size="small"
                />

                {/* Start Time */}
                <DateTimePicker
                    control={control}
                    name="downtime_start"
                    label={t('sla.start_time', 'Thời gian bắt đầu')}
                    size="small"
                    required
                />

                {/* End Time */}
                <div>
                    <DateTimePicker
                        control={control}
                        name="downtime_end"
                        label={t('sla.end_time', 'Thời gian kết thúc')}
                        size="small"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        {t(
                            'sla.leave_empty_ongoing',
                            'Để trống nếu đang diễn ra'
                        )}
                    </p>
                </div>

                {/* Description */}
                <div className="space-y-1">
                    <Label className="text-xs">
                        {t('sla.description', 'Mô tả')}
                    </Label>
                    <Controller
                        name="downtime_description"
                        control={control}
                        render={({ field }) => (
                            <TextEditorCustomize
                                value={{
                                    text: field.value || '',
                                    attachments: [],
                                }}
                                onChange={(val) => field.onChange(val.text)}
                                placeholder={t(
                                    'sla.description_placeholder',
                                    'Mô tả sự cố...'
                                )}
                                disabledAttachments
                            />
                        )}
                    />
                </div>

                {/* Root Cause */}
                <div className="space-y-1">
                    <Label className="text-xs">
                        {t('sla.root_cause', 'Nguyên nhân gốc')}
                    </Label>
                    <Controller
                        name="root_cause"
                        control={control}
                        render={({ field }) => (
                            <TextEditorCustomize
                                value={{
                                    text: field.value || '',
                                    attachments: [],
                                }}
                                onChange={(val) => field.onChange(val.text)}
                                placeholder={t('sla.root_cause_placeholder')}
                                disabledAttachments
                            />
                        )}
                    />
                </div>

                {/* Counts Against SLA */}
                <div
                    className={`flex items-center justify-between p-3 rounded-lg ${
                        countsAgainstSLA
                            ? 'bg-red-50 border border-red-200'
                            : 'bg-green-50 border border-green-200'
                    }`}
                >
                    <div className="space-y-1">
                        <Label className="text-sm font-medium">
                            {t('sla.counts_against_sla', 'Tính vào SLA')}
                        </Label>
                        <p className="text-xs text-gray-600">
                            {countsAgainstSLA
                                ? t('sla.counts_yes_hint')
                                : t('sla.counts_no_hint')}
                        </p>
                        <p className="text-xs text-blue-600">
                            {t('sla.auto_set_by_cause')}
                        </p>
                    </div>
                    <Controller
                        name="counts_against_sla"
                        control={control}
                        render={({ field }) => (
                            <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                </div>

                {/* Excluded Reason (only if not counting) */}
                {!countsAgainstSLA && (
                    <div className="space-y-1">
                        <Label className="text-xs">
                            {t('sla.excluded_reason', 'Lý do không tính')}
                        </Label>
                        <Controller
                            name="excluded_reason"
                            control={control}
                            render={({ field }) => (
                                <TextEditorCustomize
                                    value={{
                                        text: field.value || '',
                                        attachments: [],
                                    }}
                                    onChange={(val) => field.onChange(val.text)}
                                    placeholder={t(
                                        'sla.excluded_reason_placeholder'
                                    )}
                                    disabledAttachments
                                />
                            )}
                        />
                    </div>
                )}
            </form>
        </Dialog>
    )
}

export default DowntimeFormModal
